import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {ImageOpPerPixelFn} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/node"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {createImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"
import {HalCompositorInfo} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/hal"
import {ColorSpace, definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/convert-color-space/definition"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {merge} from "rxjs"
import {assertNever} from "@cm/utils"
import {ImageOpInputsOrNodes} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"

@registerNode
export class ImageOpNodeConvertColorSpace extends ImageOpPerPixelFn<typeof definition> {
    static nodeClass = "ImageOpNodeConvertColorSpace"
    static definition = definition

    constructor(parameters: ImageOpInputsOrNodes<typeof definition>) {
        super(parameters)
        merge(this.listenToParameterChange("sourceColorSpace"), this.listenToParameterChange("targetColorSpace")).subscribe(() =>
            this.requestCompositorRebuild(),
        )
    }

    protected getCompositingInfo(): HalCompositorInfo {
        return {
            compositingFunction:
                this.parameters.sourceColorSpace === this.parameters.targetColorSpace
                    ? `
                vec3 sourceData = fetchSource(0).rgb;
                return vec4(sourceData);
                `
                    : `
                vec3 sourceData = fetchSource(0).rgb;
                ${this.colorSpaceToLinearRgbFn(this.parameters.sourceColorSpace)}
                ${this.colorSpaceFromLinearRgbFn(this.parameters.targetColorSpace)}
                return vec4(targetData, 1.0);
                `,
            sourceParameterNames: ["sourceImage"],
        }
    }

    override toImgProc = async (evaluator: Evaluator<ImageOpContextImgProc>, parameters: ImageOpParametersOfType<typeof definition, "imgproc">) => {
        const {sourceImage, sourceColorSpace, targetColorSpace} = await evaluator.getAll(parameters)
        return createImageOpImgProc(sourceImage.descriptor, {
            type: "convertColorSpace",
            input: sourceImage.node,
            sourceColorSpace: sourceColorSpace,
            targetColorSpace: targetColorSpace,
        })
    }

    private colorSpaceToLinearRgbFn(colorSpace: ColorSpace) {
        switch (colorSpace) {
            case "linear":
                return `
                    vec3 linearRgb = sourceData;
                `
            case "srgb":
                return `
                    vec3 linearRgb = vec3(
                        sourceData.r < 0.04045 ? sourceData.r / 12.92 : pow((sourceData.r + 0.055) / 1.055, 2.4),
                        sourceData.g < 0.04045 ? sourceData.g / 12.92 : pow((sourceData.g + 0.055) / 1.055, 2.4),
                        sourceData.b < 0.04045 ? sourceData.b / 12.92 : pow((sourceData.b + 0.055) / 1.055, 2.4)
                    );
                `
            case "lab":
                return `
                    // convert LAB to XYZ
                    // Lab input is normalized to 0-1 (CIE L*: 0-100, a*/b*: roughly -1..1, all /100) — see colorSpaceFromLinearRgbFn below
                    float epsilon = 216.0 / 24389.0;
                    float kappa = 24389.0 / 27.0;
                    float fy = (sourceData.r * 100.0 + 16.0) / 116.0;
                    float fx = fy + sourceData.g / 5.0;
                    float fz = fy - sourceData.b / 2.0;
                    float fx3 = fx * fx * fx;
                    float fy3 = fy * fy * fy;
                    float fz3 = fz * fz * fz;
                    vec3 xyz = vec3(
                        fx3 > epsilon ? fx3 : (116.0 * fx - 16.0) / kappa,
                        sourceData.r * 100.0 > kappa * epsilon ? fy3 : sourceData.r * 100.0 / kappa,
                        fz3 > epsilon ? fz3 : (116.0 * fz - 16.0) / kappa
                    );

                    // un-normalize from D65 white point
                    xyz *= vec3(0.95047, 1.0, 1.08883);

                    // convert XYZ to linear RGB
                    vec3 linearRgb = vec3(
                         3.2404542 * xyz.r - 1.5371385 * xyz.g - 0.4985314 * xyz.b,
                        -0.9692660 * xyz.r + 1.8760108 * xyz.g + 0.0415560 * xyz.b,
                         0.0556434 * xyz.r - 0.2040259 * xyz.g + 1.0572252 * xyz.b
                    );
                `
            default:
                assertNever(colorSpace)
        }
    }

    private colorSpaceFromLinearRgbFn(colorSpace: ColorSpace) {
        switch (colorSpace) {
            case "linear":
                return `
                    vec3 targetData = linearRgb;
                `
            case "srgb":
                return `
                    vec3 clampedLinearRgb = clamp(linearRgb, 0.0, 1.0);
                    vec3 targetData = vec3(
                        clampedLinearRgb.r < 0.0031308 ? clampedLinearRgb.r * 12.92 : pow(clampedLinearRgb.r, 1.0 / 2.4) * 1.055 - 0.055,
                        clampedLinearRgb.g < 0.0031308 ? clampedLinearRgb.g * 12.92 : pow(clampedLinearRgb.g, 1.0 / 2.4) * 1.055 - 0.055,
                        clampedLinearRgb.b < 0.0031308 ? clampedLinearRgb.b * 12.92 : pow(clampedLinearRgb.b, 1.0 / 2.4) * 1.055 - 0.055
                    );
                `
            case "lab":
                return `
                    // convert linear RGB to XYZ
                    vec3 xyz = vec3(
                        0.4124564 * linearRgb.r + 0.3575761 * linearRgb.g + 0.1804375 * linearRgb.b,
                        0.2126729 * linearRgb.r + 0.7151522 * linearRgb.g + 0.0721750 * linearRgb.b,
                        0.0193339 * linearRgb.r + 0.1191920 * linearRgb.g + 0.9503041 * linearRgb.b
                    );
                    
                    // normalize for D65 white point
                    xyz /= vec3(0.95047, 1.0, 1.08883);
                    
                    // convert XYZ to LAB
                    float epsilon = 216.0 / 24389.0;
                    float kappa = 24389.0 / 27.0;
                    vec3 f = vec3(
                        xyz.r > epsilon ? pow(xyz.r, 1.0 / 3.0) : (kappa * xyz.r + 16.0) / 116.0,
                        xyz.g > epsilon ? pow(xyz.g, 1.0 / 3.0) : (kappa * xyz.g + 16.0) / 116.0,
                        xyz.b > epsilon ? pow(xyz.b, 1.0 / 3.0) : (kappa * xyz.b + 16.0) / 116.0
                    );
                    // Lab output normalized to 0-1 (CIE L*: 0-100, a*/b*: roughly -1..1, all /100) — see colorSpaceToLinearRgbFn above
                    vec3 targetData = vec3(
                        1.16 * f.y - 0.16,
                        5.0 * (f.x - f.y),
                        2.0 * (f.y - f.z)
                    );
                `
            default:
                assertNever(colorSpace)
        }
    }
}
